import { HelpCircle, FlaskConical, Lightbulb, Sprout, CheckCircle2, CircleDashed } from "lucide-react";
import {
  HubPage, Container, SectionHead, ResourceCard, CommunityCTA, BetaCTA, Band, HeroMedia, Seo,
} from "../components/hub/HubKit";
import { resourcesByType, PAGE_IMAGES } from "../data/hub";

const PILLARS = [
  { icon: FlaskConical, title: "Research findings", note: "What the evidence currently points to, in plain language." },
  { icon: Lightbulb, title: "Emerging ideas", note: "Promising directions that are still early and worth watching." },
  { icon: Sprout, title: "Everyday implications", note: "What it may mean for real, ordinary life — gently, never prescriptively." },
];

const ESTABLISHED = [
  "Consistent, sufficient sleep supports mood, memory, and health.",
  "Regular gentle movement benefits body and mind.",
  "A varied, fiber-rich diet supports digestion and overall wellbeing.",
];
const STUDYING = [
  "The 'ideal' amount of sleep, movement, or nutrients for each individual.",
  "How much houseplants really affect indoor air quality at home.",
  "Which specific gut-health and probiotic claims hold up over time.",
];
const QUESTIONS = [
  "Who funded or published this, and might that shape the findings?",
  "Is this a single early study, or a pattern across many?",
  "Does the headline promise more certainty than the research actually shows?",
  "What would this look like as a small, gentle experiment in my own life?",
];

export default function Learning() {
  const learning = resourcesByType("learning");
  return (
    <HubPage testid="learning-page">
      <Seo
        title="What We're Learning — HEARD's Plain-Language Learning Hub"
        description="Honest, plain-language research summaries from HEARD. What's established, what's still being studied, everyday implications, and questions worth asking."
      />

      {/* Editorial masthead */}
      <Band tone="sage" className="border-b border-line/60">
        <Container className="pt-14 pb-12 sm:pt-20">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_0.8fr]">
            <SectionHead
              overline="What We're Learning"
              title="We research. We listen. We share what may help."
              sub="A plain-language learning hub. We're careful to separate what's established from what's still being studied — and we never present early research as settled fact."
            />
            <HeroMedia src={PAGE_IMAGES.learning} alt="A warm wooden desk with an open journal, books and a candle in soft light" eager className="aspect-[16/10] lg:aspect-[5/4]" />
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3" data-testid="learning-pillars">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="rounded-[1.4rem] border border-line bg-white/70 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sage/12 text-sage-deep">
                  <p.icon size={20} strokeWidth={1.8} />
                </span>
                <h3 className="mt-4 font-serif text-lg font-semibold text-heading">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/70">{p.note}</p>
              </div>
            ))}
          </div>
        </Container>
      </Band>

      {/* Established vs still being studied — clearly separated, colour-coded */}
      <Container className="py-14 sm:py-20">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="established-vs-studying">
          <div className="rounded-[1.5rem] border-2 border-sage/30 bg-sage/[0.06] p-7 sm:p-8">
            <div className="flex items-center gap-2.5 text-sage-deep">
              <CheckCircle2 size={20} strokeWidth={2} />
              <h3 className="text-sm font-semibold uppercase tracking-[0.16em]">Generally established</h3>
            </div>
            <ul className="mt-5 space-y-3">
              {ESTABLISHED.map((e) => (
                <li key={e} className="flex gap-3 text-sm leading-relaxed text-ink/80">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sage-deep" />{e}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-[1.5rem] border-2 border-dashed border-taupe/40 bg-cream-deep/40 p-7 sm:p-8">
            <div className="flex items-center gap-2.5 text-taupe">
              <CircleDashed size={20} strokeWidth={2} />
              <h3 className="text-sm font-semibold uppercase tracking-[0.16em]">Still being studied</h3>
            </div>
            <ul className="mt-5 space-y-3">
              {STUDYING.map((s) => (
                <li key={s} className="flex gap-3 text-sm leading-relaxed text-ink/80">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full border border-taupe" />{s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Container>

      {/* Research summaries */}
      <Band tone="white" className="border-y border-line/60 py-14 sm:py-20">
        <Container>
          <h2 className="serif-display text-2xl text-charcoal">Research summaries</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/70">
            Each summary includes what we know, what we're still learning, everyday implications, and its sources.
          </p>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="learning-grid">
            {learning.map((r) => <ResourceCard key={r.slug} resource={r} />)}
          </div>
        </Container>
      </Band>

      {/* Questions worth asking */}
      <Container className="py-14 sm:py-20">
        <div className="rounded-[1.6rem] border border-line bg-cream-deep/40 p-7 sm:p-10" data-testid="questions-worth-asking">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage/12 text-sage-deep">
              <HelpCircle size={22} strokeWidth={1.8} />
            </span>
            <h2 className="serif-display text-2xl sm:text-3xl text-charcoal">Questions worth asking</h2>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">
            Good questions keep us honest. When you read any health or research claim — including ours — it helps to ask:
          </p>
          <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {QUESTIONS.map((q, i) => (
              <li key={q} className="flex gap-3 rounded-2xl border border-line bg-white/70 px-5 py-4 text-sm leading-relaxed text-ink/80">
                <span className="serif-display text-lg leading-none text-sage/70">{String(i + 1).padStart(2, "0")}</span>
                <span>"{q}"</span>
              </li>
            ))}
          </ul>
        </div>
      </Container>

      <Band tone="deep" className="py-14 sm:py-20">
        <Container>
          <BetaCTA />
          <div className="mt-8"><CommunityCTA /></div>
        </Container>
      </Band>
    </HubPage>
  );
}
